import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon } from "lucide-react";

import { BuildItems, CopyBuildIdButton, FindBuildHint } from "@/features/builds";

import { useAssetLookup } from "../../api/useAssetLookup";
import type { BuildAdvice } from "../../model/Analysis";
import { formatGameTime } from "../../format/formatGameTime";

const TITLE = msg({ id: "analysis.build_advice.title", message: "Try a build of strong players" });
const PLAYERS = msg({
  id: "analysis.build_advice.players",
  message:
    "{count, plural, one {# strong {hero} player uses it} other {# strong {hero} players use it}}",
});
const PLAYED_DEFAULT = msg({
  id: "analysis.build_advice.played_default",
  message: "you played with the game's default build",
});
const PLAYED_NAMED = msg({
  id: "analysis.build_advice.played_named",
  message: "you played with “{name}”",
});
const NUMBER = msg({ id: "analysis.build_advice.number", message: "No. {number}" });
const MINE_LATE = msg({ id: "analysis.build_advice.mine_late", message: "you: {time}" });
const MINE_MISSING = msg({ id: "analysis.build_advice.mine_missing", message: "not bought" });
const ALL_BUILDS = msg({ id: "analysis.build_advice.all", message: "All {hero} builds" });

type BuildAdviceCardProps = {
  advice: BuildAdvice;
  heroId: number;
};

// «Попробуй сборку сильных» в начале закупа (доска `BuildAdvice.dc.html`): сборка из игры,
// которую находят в игре по номеру, — кто с ней играет, с чем играл игрок, каких предметов
// этой сборки ему не хватило (к какой минуте их берут сильные и когда взял он), «Скопировать
// номер», все сборки героя и как найти сборку в игре.
export const BuildAdviceCard = ({ advice, heroId }: BuildAdviceCardProps) => {
  const { _ } = useLingui();
  const hero = useAssetLookup("heroes")(heroId);
  const players = _({ ...PLAYERS, values: { count: advice.players, hero: hero.name } });
  const played =
    advice.played === null
      ? null
      : advice.played.name === null
        ? _(PLAYED_DEFAULT)
        : _({ ...PLAYED_NAMED, values: { name: advice.played.name } });
  return (
    <section
      aria-label={_(TITLE)}
      data-build-advice={advice.build_id}
      className="flex flex-col gap-3.5 rounded-[10px] border border-good/40 bg-good/10 p-4"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-[13px] text-good">{_(TITLE)}</span>
        <span className="text-xl font-semibold [overflow-wrap:anywhere]">
          {advice.name}{" "}
          <span className="font-mono text-sm font-medium text-muted">
            {_({ ...NUMBER, values: { number: String(advice.build_id) } })}
          </span>
        </span>
        <span className="text-[13px] text-muted">
          {played === null ? players : `${players} · ${played}`}
        </span>
      </div>
      <BuildItems
        size={56}
        items={advice.items.map((item) => ({
          itemId: item.item_id,
          atS: item.build_at_s,
          note:
            item.bought_at_s === null
              ? _(MINE_MISSING)
              : _({ ...MINE_LATE, values: { time: formatGameTime(item.bought_at_s) } }),
        }))}
      />
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
        <CopyBuildIdButton buildId={advice.build_id} primary />
        <Link
          to="/builds"
          search={{ hero: heroId }}
          className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-accent no-underline hover:text-accent-strong"
        >
          {_({ ...ALL_BUILDS, values: { hero: hero.name } })}
          <ArrowRightIcon aria-hidden="true" className="size-4" />
        </Link>
        <FindBuildHint className="text-xs text-subtle" />
      </div>
    </section>
  );
};
